import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Tooltip, Typography } from '@mui/material'
import React from 'react'

import FlagIndicator from '@app/src/components/Flags/FlagIndicator'
import Markdown from '@app/src/components/Markdown'
import Avatar, { AvatarSize } from '@app/src/components/Ui/Avatar'
import type { TriggerInfo } from '@app/src/hooks/useExcludedQuestionTrigger'
import useExcludedQuestionTrigger from '@app/src/hooks/useExcludedQuestionTrigger'
import useQuestionRules from '@app/src/hooks/useQuestionRules'
import { useReport } from '@app/src/pages/Questionnaire/ReportContext'
import RequestItemGuidance from '@app/src/pages/Questionnaire/RequestItemGuidance'
import type { ReportFlagRule } from '@app/src/types/flags'
import { ActivationType, ReportFlagType } from '@app/src/types/flags'
import type { RequestItem as RequestItemType, ResponseItem } from '@app/src/types/resourceExplorer'
import { AltRouteOutlined, KeyboardArrowDown } from '@mui/icons-material'
import { useIntl } from 'react-intl'

type RequestItemTitleProps = {
  item: RequestItemType
  responseItem?: ResponseItem
  sectionNumber: number
  itemNumber: number
  excluded?: boolean
  children?: React.ReactNode
}

const getFlagRules = (
  item: RequestItemType,
  responseItem: ResponseItem | undefined,
  isPreview: boolean,
): ReportFlagRule[] => {
  if (isPreview)
    return item.template?.reportFlagRules?.filter(rule => rule.activationType === ActivationType.Active) ?? []
  if (responseItem?.flag === ReportFlagType.NotSet || !responseItem?.flagRule) return []
  return [responseItem.flagRule]
}

const RequestItemTitleWrapper = ({
  children,
  excluded,
  triggerInfo,
}: {
  children: React.ReactNode
  excluded: boolean
  triggerInfo?: TriggerInfo | null
}) => {
  const { formatMessage } = useIntl()
  if (excluded) {
    return (
      <Accordion
        sx={({ palette }) => ({ '&.MuiPaper-root': { borderRadius: 2, bgcolor: palette.grey[100] } })}
        elevation={0}
        disableGutters
      >
        <AccordionSummary expandIcon={<KeyboardArrowDown />}>
          <Stack
            direction="row"
            pt={2}
            pb={2}
            px={2}
            spacing={2}
            width="100%"
            justifyContent="space-between"
            alignItems="center"
          >
            {children}
            <Typography ml={2} variant="subtitle1" color="textSecondary">
              {formatMessage({ id: 'templateBuilder.questionRules.excluded' })}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {triggerInfo ? (
            <Typography>
              {triggerInfo.isSkipped
                ? formatMessage(
                    { id: 'templateBuilder.questionRules.excludedBySkipped' },
                    {
                      questionIndex: triggerInfo.questionIndex,
                      questionText: triggerInfo.questionText,
                      b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                    },
                  )
                : triggerInfo.isExcluded
                  ? formatMessage(
                      { id: 'templateBuilder.questionRules.excludedByExcluded' },
                      {
                        questionIndex: triggerInfo.questionIndex,
                        questionText: triggerInfo.questionText,
                        b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                      },
                    )
                  : triggerInfo.isFileUploaded
                    ? formatMessage(
                        { id: 'templateBuilder.questionRules.excludedByFileUploaded' },
                        {
                          questionIndex: triggerInfo.questionIndex,
                          questionText: triggerInfo.questionText,
                          b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                        },
                      )
                    : formatMessage(
                        { id: 'templateBuilder.questionRules.excludedDescription' },
                        {
                          answerValue: triggerInfo.answerValue,
                          questionIndex: triggerInfo.questionIndex,
                          questionText: triggerInfo.questionText,
                          b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                        },
                      )}
            </Typography>
          ) : (
            <Typography>
              {formatMessage({ id: 'templateBuilder.questionRules.excludedFallback' }, { isSection: false })}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    )
  }

  return (
    <Stack direction="row" py={2.5} px={2.5} spacing={2} bgcolor="grey.100" borderBottom={1} borderColor="grey.300">
      {children}
    </Stack>
  )
}

const RequestItemTitle: React.FC<RequestItemTitleProps> = ({
  item,
  responseItem,
  sectionNumber,
  itemNumber,
  excluded = false,
}) => {
  const { isPreview } = useReport()
  const flagRules = getFlagRules(item, responseItem, isPreview ?? false)
  const { rulesAffectingCurrentQuestion } = useQuestionRules(item)
  const { formatMessage } = useIntl()
  const triggerInfo = useExcludedQuestionTrigger(item, rulesAffectingCurrentQuestion)

  return (
    <RequestItemTitleWrapper excluded={excluded} triggerInfo={triggerInfo}>
      <Stack direction="row" spacing={1}>
        <Typography
          variant="subtitle1"
          color={excluded ? 'textSecondary' : 'textPrimary'}
        >{`${sectionNumber}.${itemNumber}`}</Typography>
        <Stack direction="row" alignItems="center" spacing={1} height="fit-content">
          {Boolean(flagRules.length) && <FlagIndicator flagRules={flagRules} />}
          {Boolean(item.questionnaireQuestionRules?.length) && isPreview && (
            <Tooltip arrow title={formatMessage({ id: 'questionnaireConfig.questionWithRules' })}>
              <Box>
                <Avatar
                  size={AvatarSize.Small}
                  variant="rounded"
                  sx={{
                    backgroundColor: 'grey.300',
                  }}
                >
                  <AltRouteOutlined sx={{ color: 'primary.main', fontSize: 24 }} />
                </Avatar>
              </Box>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      <Stack direction="row" sx={{ flexGrow: 1 }} justifyContent="space-between">
        <Typography variant="subtitle1" color={excluded ? 'textSecondary' : 'textPrimary'}>
          <Markdown>{item.description}</Markdown>
        </Typography>
        {!excluded && (
          <Stack sx={{ ml: 1 }}>
            <RequestItemGuidance item={item} />
          </Stack>
        )}
      </Stack>
    </RequestItemTitleWrapper>
  )
}

export default RequestItemTitle
